import { inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { map, Observable } from 'rxjs';

/**
 * Authorization guard that checks user permissions and loads them if needed.
 * Makes API call to fetch permissions on first access, then checks against route requirements.
 * Uses the route's `data.permissions` property to determine required permissions.
 * Allows access if user has at least one of the required permissions.
 * Redirects to /unauthorized if user lacks required permissions.
 *
 * @example
 * // Route configuration:
 * {
 *   path: 'bic',
 *   canActivate: [authGuard],
 *   data: { permissions: ['READALL', 'EDITALL'] },
 *   loadChildren: ...
 * }
 *
 * @returns Observable<boolean> or boolean - true if access granted, false with redirect if denied
 */
export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean> | boolean => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);

  // Get required permissions from route data
  const requiredPermissions = route.data?.['permissions'] as
    | string[]
    | undefined;

  // If no permissions specified, allow access (public route)
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  // If permissions already loaded, check immediately
  if (authService.arePermissionsLoaded()) {
    const hasAccess = authService.hasAnyPermission(requiredPermissions);
    if (!hasAccess) {
      router.navigate(['/unauthorized']);
      return false;
    }
    return true;
  }

  // Permissions not loaded yet - load them first, then check
  return authService.loadAndSetPermissions().pipe(
    map(() => {
      const hasAccess = authService.hasAnyPermission(requiredPermissions);
      if (!hasAccess) {
        router.navigate(['/unauthorized']);
        return false;
      }
      return true;
    }),
  );
};
